import { useEffect, useMemo, useState } from 'react';
import { ThemeRoot } from '@nyce/admin-kit';
import { Button } from '@nyce/admin-kit/ui';
import { ApiError } from '@nyce/admin-kit/api';
import { logos } from './assets';
import { api } from './session';
import { browser, startMcpSso } from './sso';
import type { LoginOptions, OAuthClientInfo, OAuthRequest } from './api';
import { ThemeToggle, useTheme } from './theme';

const PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'resource',
] as const;

/** The authorization request in the URL, as the MCP client sent it. */
function readRequest(search: string): OAuthRequest {
  const q = new URLSearchParams(search);
  const r: Record<string, string> = {};
  for (const k of PARAMS) {
    const v = q.get(k);
    if (v != null) r[k] = v;
  }
  return r as unknown as OAuthRequest;
}

/** An error the hub answers with a `redirect` belongs to the client (RFC 6749 §4.1.2.1): the browser goes back with it. */
function redirectOf(e: unknown): string | null {
  const body = e instanceof ApiError ? (e.body as { redirect?: unknown } | undefined) : undefined;
  return typeof body?.redirect === 'string' ? body.redirect : null;
}

function messageOf(e: unknown): string {
  const body = e instanceof ApiError ? (e.body as { error_description?: unknown } | undefined) : undefined;
  if (typeof body?.error_description === 'string') return body.error_description;
  return e instanceof Error ? e.message : 'Something went wrong';
}

/**
 * MCP consent (docs/mcp.md): an MCP client (Claude) asks to use the Operations Hub as the operator. Allow signs in with
 * Microsoft (AUTH `/authorize`, operator tenant; the console's own session is not touched); the hub redeems that sign-in
 * for the client and the browser goes back to it. Deny goes back with `access_denied`.
 */
export function OAuthAuthorize() {
  const { resolved } = useTheme();
  const request = useMemo(() => readRequest(window.location.search), []);
  const loginError = useMemo(() => new URLSearchParams(window.location.search).get('login_error'), []);
  const [client, setClient] = useState<OAuthClientInfo | null>(null);
  const [sso, setSso] = useState<LoginOptions['sso'] | undefined>(undefined);
  const [error, setError] = useState<string | null>(loginError);
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);

  useEffect(() => {
    api
      .oauthCheck(request)
      .then(setClient)
      .catch((e: unknown) => {
        const back = redirectOf(e);
        if (back) browser.go(back);
        else setError(messageOf(e));
      });
    api
      .loginOptions()
      .then((o) => setSso(o.sso ?? null))
      .catch(() => setSso(null));
  }, [request]);

  const allow = async () => {
    if (!sso) return;
    setBusy('allow');
    setError(null);
    try {
      await startMcpSso(sso, request);
    } catch (e) {
      setBusy(null);
      setError(messageOf(e));
    }
  };

  const deny = async () => {
    setBusy('deny');
    try {
      browser.go((await api.oauthApprove({ ...request, deny: true })).redirect);
    } catch (e) {
      setBusy(null);
      setError(messageOf(e));
    }
  };

  const name = client?.clientName ?? 'An application';
  return (
    <div className="nyk-login">
      <ThemeToggle className="nyk-login-theme" />
      <ThemeRoot theme={resolved} style={{ background: 'transparent' }}>
        <div className="nyk-ui-login" data-testid="oauth-authorize">
          <header className="nyk-ui-header">
            <img
              src={resolved === 'dark' ? logos.logoWhite : logos.logoBlack}
              alt="nyceOperations"
              className="nyk-login-logo oph-logo"
            />
          </header>
          <div className="nyk-ui-login-form">
            {client && (
              <>
                <h2 className="oph-consent-title">Connect {name}</h2>
                <p className="oph-consent-text">
                  <b>{name}</b> ({client.redirectHost}) wants to use the NYCE Operations Hub as you, over MCP.
                  It can read and do what your account may do here (your Operations Hub permissions) until you
                  disconnect it there.
                </p>
              </>
            )}
            {sso === null && !error && (
              <div className="nyk-ui-login-error" role="alert">
                MCP connections need the Microsoft sign-in, which is not configured for this hub.
              </div>
            )}
            {error && (
              <div className="nyk-ui-login-error" role="alert">
                {error}
              </div>
            )}
            {client && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  loading={busy === 'allow'}
                  disabled={!sso || busy !== null}
                  onClick={() => void allow()}
                >
                  Sign in with Microsoft and allow
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  loading={busy === 'deny'}
                  disabled={busy !== null}
                  onClick={() => void deny()}
                >
                  Deny
                </Button>
              </>
            )}
          </div>
        </div>
      </ThemeRoot>
    </div>
  );
}
